import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OfflineChangesService } from 'projects/phdsoft/common/src/lib/shared/services/offline-changes.service';
import { OfflineService } from 'projects/phdsoft/common/src/lib/shared/services/offline.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IInspectionPlan } from './inspection-plan.interface';
import { localDayToUtcIso } from 'src/app/shared/utils/calendar-day.util';

// Business Rule Error Codes
export const BUSINESS_RULE_ERRORS = {
  MULTIPLE_PLAN_COMPLETION_WITH_MULTIPLE_FEA_TRIGGERS: 'MULTIPLE_PLAN_COMPLETION_WITH_MULTIPLE_FEA_TRIGGERS'
} as const;

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  code?: string;
  traceId?: string;
  planCount?: number;
  feaTriggered?: boolean;
}

/**
 * Type guard to check if response is a ProblemDetails error
 */
export function isProblemDetails(response: any): response is ProblemDetails {
  return response && 
         typeof response.type === 'string' &&
         typeof response.title === 'string' &&
         typeof response.status === 'number';
}

@Injectable({
  providedIn: 'root'
})
export class InspectionPlanService {
  constructor(
    private http: HttpClient,
    private readonly offlineChangesService: OfflineChangesService,
    private readonly offlineService: OfflineService
  ) { }

  // A calendar day goes to the API as that day's UTC midnight (calendar-day.util.ts).
  private formatDateToBackend(date: Date): string {
    return localDayToUtcIso(date);
  }

  getAssetInspectionPlans(assetId: string) {
    const timestamp = new Date().getTime();
    return this.http.get<IInspectionPlan[]>(
      `${environment.API_INSPEC}InspectionPlan/GetAllInspectionPlans/${assetId}?t=${timestamp}`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      }
    );
  }

  getInspectionPlanByRegionId(assetId: string, regionIds: string[]): Observable<IInspectionPlan[]> {
    if (window.navigator.onLine) {
      return this.http.post<IInspectionPlan[]>(environment.API_INSPEC + `InspectionPlan/GetInspectionPlansByRegions/${assetId}`, regionIds);
    } else {
      return new Observable<IInspectionPlan[]>(observer => {
        this.getHistoricalPlansOffline(assetId, regionIds).then(plans => {
          observer.next(plans);
          observer.complete();
        }).catch(error => {
          observer.error(error);
        });
      });
    }
  }

  private async getHistoricalPlansOffline(assetId: string, regionIds: string[]): Promise<IInspectionPlan[]> {
    try {
      let allHistoricalPlans: IInspectionPlan[] = [];
      
      for (const regionId of regionIds) {
        const historicalPlansData = await this.offlineService.getItem(`historicalPlans_${assetId}_${regionId}`);
        if (historicalPlansData && historicalPlansData.json) {
          const regionPlans = Array.isArray(historicalPlansData.json) ? historicalPlansData.json : [historicalPlansData.json];
          allHistoricalPlans = allHistoricalPlans.concat(regionPlans);
        }
      }
      
      return allHistoricalPlans;
    } catch (error) {
      console.error('Error getting historical plans offline:', error);
      return [];
    }
  }

  getOpenedInspectionPlanByRegionId(assetId: string, regionIds: string[]) {
    const preventCache = (new Date()).getTime();
    return this.http.post<IInspectionPlan[]>(environment.API_INSPEC + `InspectionPlan/GetOpenInspectionPlansByRegions/${assetId}?cache_bump=${preventCache}`, regionIds)
  }

  getInspectionPlan(id: string, countElements: boolean) {
    return this.http.get<IInspectionPlan>(environment.API_INSPEC + `InspectionPlan?id=${id}&countElements=${countElements}`)
  }

  requestApproval(id: string) {
    return this.http.get(environment.API_INSPEC + `InspectionPlan/RequestApproval/${id}`)
  }

  delete(scheduleId: string, assetId: string) {
    return this.http.delete<boolean>(
      environment.API_ADMIN + `Schedule?id=${scheduleId}&assetId=${assetId}`
    )
  }

  update(inspectionPlan: IInspectionPlan) {
    return this.http.put(environment.API_INSPEC + `InspectionPlan`, inspectionPlan)
  }

  createSingle(inspectionPlan: IInspectionPlan) {
    return this.http.post(
      environment.API_INSPEC + 'InspectionPlan', inspectionPlan
    )
  }

  managePlan(assetId: string, regionId: string, inspectionTypeId: string, date: Date, executed: boolean, inspectionTypeName: string): Observable<any> {
    const url = environment.API_INSPEC + `InspectionPlan/ManagePlan?assetId=${assetId}&regionId=${regionId}&inspectionTypeId=${inspectionTypeId}&date=${date.toDateString()}&executed=${executed}&inspectionTypeName=${inspectionTypeName}`
    if (window.navigator.onLine) {
      return this.http.post(url, null)
    }
    else {
      this.offlineChangesService.addItem("inspectionElement", url, "post", null)
      return of('true')
    }
  }

  managePlanDeletion(assetId: string, regionId: string, inspectionTypeId: string, pdate: Date, executed: boolean) {
    const date = this.formatDateToBackend(pdate);

    return this.http.post(
      environment.API_INSPEC + `InspectionPlan/ManagePlanDeletion?assetId=${assetId}&regionId=${regionId}&inspectionTypeId=${inspectionTypeId}&date=${date}&executed=${executed}`, null
    )
  }

  completePlan(planId: string) {
    return this.completeMultiplePlans([planId]);
  }

  completeMultiplePlans(planIds: string[]): Observable<any> {
    return this.http.post<any>(
      environment.API_INSPEC + `InspectionPlan/CompletePlan`, planIds
    ).pipe(
      catchError((error: any) => {
        const problemDetails = this.extractProblemDetails(error);

        if (problemDetails) {
          // Handle known business rule violations - return as value to preserve state
          if (problemDetails.code === BUSINESS_RULE_ERRORS.MULTIPLE_PLAN_COMPLETION_WITH_MULTIPLE_FEA_TRIGGERS) {
            return of(problemDetails);
          }

          // Handle other ProblemDetails responses (400, 500, etc.) - throw them
          return throwError(() => this.createStructuredError(problemDetails));
        }

        // Re-throw unexpected errors (network errors, etc.)
        return throwError(() => error);
      })
    );
  }

  private extractProblemDetails(error: any): ProblemDetails | null {
    const problemDetails = error?.error;
    
    // Validate that this is a ProblemDetails object
    if (problemDetails && 
        typeof problemDetails.type === 'string' &&
        typeof problemDetails.title === 'string' &&
        typeof problemDetails.status === 'number') {
      return problemDetails;
    }
    
    return null;
  }

  private createStructuredError(problemDetails: ProblemDetails): Error {
    const error = new Error(problemDetails.detail || problemDetails.title);
    (error as any).problemDetails = problemDetails;
    (error as any).status = problemDetails.status;
    (error as any).code = problemDetails.code;
    return error;
  }

  getCurrentInspectionPlanByRegionId(data: any) {
    return this.http.post<IInspectionPlan[]>(environment.API_INSPEC + `InspectionPlan/GetCurrentInspectionPlan`, data)
  }

  createActionPlan(inspectionId: string, anomalyActionId: string, follouUpDate: Date, assetId: string) {
    let result = this.http.post(
      environment.API_INSPEC + `InspectionPlan/CreateActionPlan?inspectionId=${inspectionId}&anomalyActionId=${anomalyActionId}&follouUpDate=${follouUpDate.toDateString()}&assetId=${assetId}`, {}
    );
    return result
  }

  /** Under Plan Management the section names the repair's Plan Item; legacy plans ignore it. */
  createActionPlanForRepair(repairId: string, anomalyActionId: string, follouUpDate: Date, assetId: string, regionId: string, sectionId: string) {
    return this.http.post(
      environment.API_INSPEC + `InspectionPlan/CreateActionPlanForRepair?repairId=${repairId}&anomalyActionId=${anomalyActionId}&follouUpDate=${follouUpDate.toDateString()}&assetId=${assetId}&regionId=${regionId}&sectionId=${sectionId ?? ''}`, {}
    ).toPromise();
  }

  /** Under Plan Management the asset decides without a scan of every plan. */
  removeRepairFromPlan(repairId: string, assetId: string) {
    return this.http.post(
      environment.API_INSPEC + `InspectionPlan/RemoveRepairFromPlan?repairId=${repairId}&assetId=${assetId}`, {}
    );
  }

  createActionPlanForProtection(protectionData: any) {
    return this.http.post(
      environment.API_INSPEC + `InspectionPlan/CreateActionPlanForProtection`, protectionData
    ).toPromise();
  }

  /** Under Plan Management no plan holds a protection; the asset lets the backend say so without a scan. */
  removeProtectionElementFromPlan(protectionId: string, assetId: string) {
    return this.http.post(
      environment.API_INSPEC + `InspectionPlan/RemoveProtectionElementFromPlan?elementId=${protectionId}&assetId=${assetId}`, {}
    ).toPromise();
  }

  getTargetsInspections(assetId: string, targetsIds: string[],referenceDate:string,regionsIds: string[], sectionId: string){
    const params = new HttpParams()
    .set('assetId', assetId)
    // .set('targetsIds', targetsIds.join(','))
    .set('referenceDate', referenceDate)
    .set('regionsIds', regionsIds.join(','))
    .set('sectionId', sectionId)


    return this.http.get<void>(`${environment.API_INSPEC}Inspection/GetTargetsInspections`, {params: params})
  }
}
